import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FoodRoute } from '../../types';

interface ProcessDetailData {
  corePrinciple: string;
  keyPoints: string[];
  technicalPoints: string[];
  comparison?: {
    title: string;
    headers: string[];
    rows: string[][];
  };
  flowChart?: string;
  notes?: string[];
}

const processDetailsMap: Record<string, ProcessDetailData> = {
  'soy-sauce-high-salt': {
    corePrinciple: '大豆蛋白质 → 米曲霉蛋白酶水解 → 氨基酸（鲜味来源）\n小麦淀粉 → 淀粉酶水解 → 糖分 → 酵母/乳酸菌发酵 → 醇、酸、酯（香气来源）',
    keyPoints: [
      '制曲是灵魂：米曲霉孢子数需达到6×10⁹/g（干基）',
      '制曲过程中16h和24h各翻曲一次，防止"烧曲"',
      '水分控制在20%~30%',
      '盐水浓度是双刃剑：过高抑制酶活，过低易杂菌繁殖',
      '酱醅pH维持在6.5~6.8，蛋白酶和谷氨酰胺酶活性最佳',
    ],
    technicalPoints: [
      '原料：大豆 + 小麦',
      '盐浓度：17%~18%',
      '物态：稀醪（液态）',
      '发酵温度：≤30℃，低温长周期',
      '发酵周期：3~6个月',
      '风味：酱香浓郁，口感醇厚',
    ],
    comparison: {
      title: '高盐稀态 vs 低盐固态发酵对比',
      headers: ['对比项', '高盐稀态发酵', '低盐固态发酵'],
      rows: [
        ['原料', '大豆 + 小麦', '脱脂大豆 + 麸皮'],
        ['盐浓度', '17%~18%', '6%~8%'],
        ['物态', '稀醪（液态）', '固态酱醅'],
        ['发酵温度', '≤30℃', '42~45℃'],
        ['发酵周期', '3~6个月', '15~30天'],
        ['风味', '酱香浓郁，口感醇厚', '鲜味够但香气不足'],
        ['市场占比', '约30%（优质酱油）', '约70%（中低端为主）'],
      ],
    },
    notes: [
      '高盐稀态前期必须勤淋油：曲霉孢子疏水，表层曲料难与盐水融合',
      '盐浓度偏低易污染耐盐杂菌',
      '客观说：低盐固态工艺虽然快、成本低，但风味差距是客观存在的',
    ],
  },
  'soy-sauce-low-salt': {
    corePrinciple: '大豆蛋白质 → 米曲霉蛋白酶水解 → 氨基酸（鲜味来源）\n小麦淀粉 → 淀粉酶水解 → 糖分 → 酵母/乳酸菌发酵 → 醇、酸、酯（香气来源）',
    keyPoints: [
      '前期水解（42~45℃，约10天）：蛋白酶分解蛋白→氨基酸',
      '后期发酵（30~32℃，约14~20天）：补浓盐水至15%',
      '加耐盐酵母/乳酸菌 → 产香',
      '盐水浓度控制在11~13°Bé',
    ],
    technicalPoints: [
      '原料：脱脂大豆 + 麸皮',
      '盐浓度：6%~8%',
      '物态：固态酱醅',
      '发酵温度：42~45℃（前期）',
      '发酵周期：15~30天',
      '风味：鲜味够但香气不足，略带焦苦',
    ],
    notes: [
      '生产效率高，适合大规模工业化生产',
      '成本较低，市场占比约70%',
      '风味相对简单，适合中低端市场',
    ],
  },
  'beer-lager': {
    corePrinciple: '麦芽淀粉 → 糖化 → 麦芽糖等可发酵糖 → 酵母发酵 → 乙醇 + CO₂ + 风味物质\n啤酒花 → 提供苦味、香气、抑菌保鲜',
    keyPoints: [
      '糖化温度谱是啤酒风格的第一决定因素',
      '酵母纯度与活性：必须用纯粹培养酵母',
      '每批次麦汁组成应均匀（10°P麦汁浓度偏差±0.2）',
      '发酵罐温控：高泡期产热巨大，冷却必须跟得上',
      '溶氧控制：麦汁冷却后入罐前需适量充氧（8~12mg/L）',
    ],
    technicalPoints: [
      '发酵类型：下面发酵',
      '发酵温度：8~13℃',
      '发酵周期：5~10天',
      '贮酒温度：0℃左右',
      '贮酒周期：1~2个月',
    ],
    comparison: {
      title: '拉格 vs 艾尔啤酒对比',
      headers: ['对比项', '拉格啤酒', '艾尔啤酒'],
      rows: [
        ['发酵类型', '下面发酵', '上面发酵'],
        ['发酵温度', '8~13℃', '15~24℃'],
        ['发酵周期', '5~10天', '3~7天'],
        ['口感特点', '清爽、酒体轻盈', '风味复杂、果香浓郁'],
        ['酵母位置', '发酵结束沉于底部', '发酵结束浮于表面'],
      ],
    },
    notes: [
      '糖化决定"有什么"，发酵决定"变成什么"，后熟决定"好不好喝"',
      '氧化是啤酒老化的头号敌人',
      '发酵开始后必须严格隔氧',
    ],
  },
  'beer-ale': {
    corePrinciple: '麦芽淀粉 → 糖化 → 麦芽糖等可发酵糖 → 酵母发酵 → 乙醇 + CO₂ + 风味物质\n啤酒花 → 提供苦味、香气、抑菌保鲜',
    keyPoints: [
      '较高温度发酵，风味物质更丰富',
      '发酵周期短，生产效率高',
      '适合精酿啤酒多样化风格',
      '酵母发酵结束浮于液面',
    ],
    technicalPoints: [
      '发酵类型：上面发酵',
      '发酵温度：15~24℃',
      '发酵周期：3~7天',
      '口感特点：风味复杂、果香浓郁',
    ],
    notes: [
      '适合追求风味复杂度的精酿啤酒',
      '发酵温度较高，需注意杂菌污染',
      '保质期相对较短',
    ],
  },
  'baijiu-maotai': {
    corePrinciple: '谷物淀粉 → 大曲/小曲中微生物酶系糖化 → 淀粉→葡萄糖 → 酵母发酵 → 乙醇 + 多种微量成分\n**白酒与啤酒/葡萄酒的根本区别**：糖化和发酵是**同时进行**的（边糖化边发酵）',
    keyPoints: [
      '大曲是白酒的"基因库"：制曲温度直接决定微生物区系',
      '高温大曲富集耐热芽孢杆菌→酱香',
      '堆积发酵是酱香型独有且不可省略的',
      '没有堆积就没有足够的微生物富集',
      '蒸馏摘酒要"量质摘酒"',
    ],
    technicalPoints: [
      '工艺：12987工艺',
      '1年1个生产周期',
      '2次投料（下沙 + 糙沙）',
      '9次蒸煮',
      '8次发酵',
      '7次取酒',
      '窖藏：3~5年',
    ],
    comparison: {
      title: '酱香型 vs 浓香型白酒对比',
      headers: ['对比项', '酱香型', '浓香型'],
      rows: [
        ['曲', '高温大曲（60~70℃以上）', '中高温大曲（55~60℃）'],
        ['窖', '石壁泥底', '泥窖'],
        ['发酵', '堆积+窖内，8次', '纯窖内，60~70天/轮'],
        ['投料', '2次', '续渣法，持续循环'],
        ['核心风味', '酱香，芽孢杆菌主导', '窖香（己酸乙酯），己酸菌主导'],
        ['周期', '1年生产+3~5年窖藏', '约2个月/轮+1~3年窖藏'],
      ],
    },
    notes: [
      '阴阳发酵：阳发酵（堆积）采集空气微生物，阴发酵（入窖）深度转化',
      '4~5轮取酒（大回酒）是酒质最好的两轮',
      '客观说：酱香12987工艺确实复杂，适合追求极致复杂风味的场景',
    ],
  },
  'baijiu-luzhou': {
    corePrinciple: '谷物淀粉 → 大曲/小曲中微生物酶系糖化 → 淀粉→葡萄糖 → 酵母发酵 → 乙醇 + 多种微量成分\n**浓香型核心**：泥窖发酵，窖泥中的己酸菌、丁酸菌代谢产生己酸乙酯、丁酸乙酯',
    keyPoints: [
      '窖池是浓香型的命根子',
      '老窖的窖泥微生物多样性远高于新窖',
      '"千年老窖"不是噱头，是生态学事实',
      '万年糟（续渣法）：酒醅不丢弃，加入新粮继续发酵',
      '混蒸混烧：新粮与酒醅同蒸',
    ],
    technicalPoints: [
      '发酵类型：泥窖固态发酵',
      '窖池：泥窖',
      '发酵周期：60~70天/轮',
      '窖藏：1~3年',
      '核心风味：窖香（己酸乙酯）',
    ],
    notes: [
      '黄水：窖底积聚的黄色淋浆水，含残余淀粉、酒精、己酸菌等',
      '是培养人工老窖的宝贝',
      '如果追求清爽窖香，浓香型是更合理的选择',
    ],
  },
  'cheese-natural': {
    corePrinciple: '牛乳中的**酪蛋白** → 凝乳酶作用 → 形成网状凝胶（凝乳）\n凝乳 → 切割排乳清 → 压榨成型 → 盐渍 → 成熟（后熟）→ 奶酪\n**一句话理解**：10斤牛奶 ≈ 1斤奶酪，奶酪是牛奶的精华浓缩',
    keyPoints: [
      '抗生素检测是红线：哪怕微量青霉素残留都会让发酵剂全军覆没',
      '凝乳酶的效价和用量必须精确',
      '切割时机和尺寸直接决定成品含水率',
      '升温速率要慢：每2min升1℃是经验值',
      '成熟环境温湿度控制至关重要',
    ],
    technicalPoints: [
      '凝乳温度：30~32℃',
      '凝乳时间：25~40min',
      '切割尺寸：1.0~1.5cm方块',
      '升温目标：37~40℃',
      '盐渍：10~15℃，20%~22%盐水浸2~4天',
      '成熟：4~6月，13~15℃，RH 80%~85%',
    ],
    notes: [
      '升温太快→凝块表面硬化→内部乳清排不出→"外干内湿"',
      '温度偏高→成熟过快，风味粗糙甚至变质',
      '湿度偏低→奶酪失水干裂；湿度偏高→表面霉变',
      '奶酪工艺本质：让牛奶凝固→把水挤出去→加盐防腐→让微生物慢慢加工出风味',
    ],
  },
  'huangjiu-tanfan': {
    corePrinciple: '糯米淀粉 → 麦曲/酒药中霉菌（根霉、毛霉、米曲霉）糖化 → 葡萄糖 → 酵母菌发酵 → 乙醇 + 有机酸 + 氨基酸 + 酯类\n**与白酒的关键区别**：黄酒是**酿造酒**（发酵后直接取酒，不蒸馏）',
    keyPoints: [
      '开耙是黄酒酿造的"定海神针"',
      '糖化（产糖）和发酵（耗糖）必须同步平衡',
      '开耙过早→发酵过猛→酒体粗糙',
      '开耙过晚→温度失控→菌体自溶→异杂味',
      '全靠老师傅的手感和经验判断',
    ],
    technicalPoints: [
      '发酵类型：边糖化边发酵（复式发酵）',
      '浸米时间：2~3天，保留浆水',
      '前发酵：开耙控制',
      '后酵：70~80天',
      '陈酿：3年以上',
    ],
    comparison: {
      title: '三种黄酒酿造工艺对比',
      headers: ['对比项', '淋饭法', '摊饭法', '喂饭法'],
      rows: [
        ['用途', '制酒母', '商品酒（品质最优）', '商品酒'],
        ['发酵周期', '约20天', '80~90天', '60~70天'],
        ['投料方式', '一次性', '一次性', '分批多次'],
        ['操作难度', '较低', '极高（开耙是核心难点）', '中等'],
        ['风味', '清淡（作为酒母）', '鲜灵、柔和、甘润、醇厚', '醇厚适中，出酒率较高'],
      ],
    },
    notes: [
      '浆水的"以酸制酸"：浸米浆水中的乳酸是天然防腐剂',
      '陶坛陈酿不可省：陶坛微透气，酒液在微氧化环境中缓慢酯化',
      '三种工艺各有分工：淋饭制酒母，摊饭出极致品质，喂饭兼顾品质和效率',
    ],
  },
  'wine-red': {
    corePrinciple: '葡萄中的葡萄糖 + 果糖 → 酵母菌酒精发酵 → 乙醇 + CO₂ + 甘油 + 风味物质\n葡萄皮中的花色苷、单宁 → 浸渍提取 → 颜色、结构感、抗氧化性',
    keyPoints: [
      '葡萄品质是天花板："七分原料，三分工艺"',
      'SO₂用量是红线：太少→氧化变质；太多→漂白花色苷',
      '浸渍管理是红酒品质的分水岭',
      '压帽/循环泵送的频率和力度决定颜色深度与单宁结构',
      '氧化是最大的敌人',
    ],
    technicalPoints: [
      '发酵类型：带皮酒精发酵',
      '发酵温度：24~28℃',
      '发酵时间：8~10天',
      '浸皮时间：7~15天',
      'MLF温度：18~20℃',
      '陈酿温度：12~15℃',
    ],
    comparison: {
      title: '红葡萄酒 vs 白葡萄酒对比',
      headers: ['对比项', '红葡萄酒', '白葡萄酒'],
      rows: [
        ['发酵基质', '果汁+果皮籽（带皮发酵）', '纯果汁（皮汁分离后发酵）'],
        ['浸渍', '有（从皮中提取颜色、单宁、香气）', '无或极短'],
        ['发酵温度', '24~28℃', '14~18℃（低温保果香）'],
        ['苹果酸-乳酸发酵', '通常进行', '通常不进行'],
        ['橡木桶陈酿', '常用', '部分品种使用'],
        ['核心风味来源', '果皮浸渍+橡木桶+MLF', '品种果香+发酵工艺'],
      ],
    },
    notes: [
      '葡萄酒是所有发酵饮品中对"原料-工艺-环境"三者协调要求最高的',
      '啤酒可以靠配方控制，白酒可以靠窖池和勾调弥补',
      '但葡萄酒一旦葡萄不好，酿酒师再强也酿不出好酒',
    ],
  },
  'wine-white': {
    corePrinciple: '葡萄中的葡萄糖 + 果糖 → 酵母菌酒精发酵 → 乙醇 + CO₂ + 甘油 + 风味物质\n白葡萄酒：皮汁分离后纯果汁发酵，低温保果香',
    keyPoints: [
      '低温发酵保香：14~18℃',
      '皮汁分离要及时，避免过多单宁',
      '澄清处理很重要',
      '抗氧化控制严格',
    ],
    technicalPoints: [
      '发酵类型：纯果汁发酵',
      '发酵温度：14~18℃',
      '发酵时间：2~3周',
      '静置澄清：8℃静置24h',
      '酒泥陈酿：可选，3~10个月',
    ],
    notes: [
      '清爽细腻、果香突出',
      '适合年轻时饮用',
      '部分品种可使用橡木桶陈酿',
    ],
  },
  'vinegar-shanxi': {
    corePrinciple: '高粱淀粉 → 大曲多菌系糖化 → 葡萄糖 → 酵母菌酒精发酵 → 乙醇\n乙醇 → 醋酸菌氧化 → 醋酸（酸味核心）\n**醋酒同源**：先酿酒，再酿醋。醋的前半段就是酿酒，后半段才把酒变成醋。',
    keyPoints: [
      '大曲是老陈醋的"基因"：以70%大麦+30%豌豆制曲',
      '红心曲（含红曲菌）是山西醋的特色',
      '醋酸发酵的翻醅不可偷懒：每日翻醅是固态发酵的命门',
      '加盐时机必须精准：第8天左右醋酸成熟即加盐',
      '"夏伏晒冬捞冰"不可替代',
    ],
    technicalPoints: [
      '酒精发酵：液态，约15天',
      '醋酸发酵：固态，9~10天',
      '熏醅温度：85℃左右，5天',
      '陈酿：夏伏晒、冬捞冰，1年以上',
      '大曲用量：62.5%',
    ],
    notes: [
      '翻不到位=供氧不足=醋酸菌罢工=出酸不足',
      '晚了→醋酸被过度氧化为CO₂和H₂O；早了→酸度不够',
      '熏醅温度低了熏香不足，高了碳化焦苦',
      '日晒蒸发水分和低沸点刺激性酸类、促进酯化产香',
      '捞冰浓缩精华',
      '山西老陈醋传统工艺需要至少400天',
    ],
  },
};

interface FoodProcessDetailProps {
  route: FoodRoute;
  onClose: () => void;
}

const FoodProcessDetail: React.FC<FoodProcessDetailProps> = ({ route, onClose }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const detailData = processDetailsMap[route.id];

  if (!detailData) {
    return (
      <Card sx={{ height: '100%', backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff' }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary">
            暂无该工艺的详细信息
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            sx={{ color: isDarkMode ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}
          >
            {route.name} - 工艺详情
          </Typography>
          <Tooltip title="关闭详情">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 2, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? '#ffb74d' : '#ff9800',
              mb: 1,
            }}
          >
            核心原理
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
            >
              {detailData.corePrinciple}
            </Typography>
          </Paper>
        </Box>

        {detailData.technicalPoints && detailData.technicalPoints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#81d4fa' : '#0288d1',
                mb: 1,
              }}
            >
              技术参数
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {detailData.technicalPoints.map((point, index) => (
                <Chip
                  key={index}
                  label={point}
                  size="small"
                  sx={{
                    backgroundColor: isDarkMode ? '#0d47a1' : '#e3f2fd',
                    color: isDarkMode ? '#90caf9' : '#1976d2',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {detailData.keyPoints && detailData.keyPoints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ce93d8' : '#9c27b0',
                mb: 1,
              }}
            >
              关键技术点
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {detailData.keyPoints.map((point, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: isDarkMode ? '#4a148c' : '#f3e5f5',
                    borderRadius: 1,
                    borderLeft: '4px solid',
                    borderColor: isDarkMode ? '#ce93d8' : '#9c27b0',
                  }}
                >
                  <Typography variant="body2">{point}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {detailData.comparison && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#66bb6a' : '#2e7d32',
                mb: 1,
              }}
            >
              {detailData.comparison.title}
            </Typography>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                backgroundColor: isDarkMode ? '#2a2a2a' : '#fafafa',
                border: '1px solid',
                borderColor: isDarkMode ? '#444444' : '#e0e0e0',
              }}
            >
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ backgroundColor: isDarkMode ? '#1b5e20' : '#e8f5e9' }}>
                    {detailData.comparison.headers.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#66bb6a' : '#2e7d32',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                  {detailData.comparison.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          sx={{
                            fontWeight: cellIndex === 0 ? 600 : 400,
                            color: cellIndex === 0
                              ? (isDarkMode ? '#ffb74d' : '#ff9800')
                              : 'text.primary',
                          }}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {detailData.notes && detailData.notes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ffb74d' : '#ff9800',
                mb: 1,
              }}
            >
              注意事项
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {detailData.notes.map((note, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: isDarkMode ? '#4a3728' : '#fff3e0',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    • {note}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              发酵类型:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}
            >
              {route.fermentationType}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              生产周期:
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? '#81d4fa' : '#0288d1', fontWeight: 600 }}
            >
              {route.productionCycle}
            </Typography>
          </Box>
          {route.temperature && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                温度:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? '#ffb74d' : '#ff9800', fontWeight: 600 }}
              >
                {route.temperature}
              </Typography>
            </Box>
          )}
        </Box>

        {route.keyMicroorganisms && route.keyMicroorganisms.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              关键微生物:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.keyMicroorganisms.map((item, index) => (
                <Chip
                  key={index}
                  label={item}
                  size="small"
                  sx={{
                    backgroundColor: isDarkMode ? '#4a148c' : '#f3e5f5',
                    color: isDarkMode ? '#e1bee7' : '#7b1fa2',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FoodProcessDetail;
